// 사업별·부서별 현황 표 — 기존 상세 현황과 같은 자료(사업·부서 한 줄씩)를 진행률 막대로 보인다
// 핵심 과제 현황은 따로 되돌릴 수 있어야 하는 화면이라 그 파일에 기대지 않고 막대 칸을 따로 둔다.

import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import type { TaskSummaryTotalBusinessItem, TaskSummaryTotalDepartmentItem } from '@/api'
import RateBar from '../../StrategyDashboard/components/RateBar'
import { formatNumber, strategyColors } from '../../StrategyDashboard/strategyMetrics'

const CELL_PADDING = '10px 8px'
// 사업·부서가 많아도 아래 요약·목록까지 내려갈 수 있게 표 높이를 제한한다 (기존 표 10줄 높이)
const MAX_TABLE_HEIGHT = 520

// 데이터 없음: 회색 빗금 막대
const NO_DATA_PATTERN = `repeating-linear-gradient(135deg, ${designTokens.colors.gray[2]} 0 3px, ${designTokens.colors.gray[0]} 3px 7px)`

// 막대와 수치를 테두리로 묶어, 옆 칸과 헷갈리지 않게 한다 (데이터 없음일 때 특히)
const RATE_BOX_STYLE = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 38,
  padding: '6px 10px',
  border: `1px solid ${strategyColors.border}`,
  borderRadius: 6,
}

/** 예산 금액은 길어서 만·억 단위로 줄인다 */
const formatAmount = (value: number): string => {
  if (Math.abs(value) >= 100_000_000) return `${Number((value / 100_000_000).toFixed(1))}억`
  if (Math.abs(value) >= 10_000) return `${Number((value / 10_000).toFixed(1))}만`
  return formatNumber(value)
}

type SummaryItem = TaskSummaryTotalBusinessItem | TaskSummaryTotalDepartmentItem

interface RateValue {
  key: string
  label: string
  /** 해당 데이터가 없으면 null (빗금 막대) */
  rate: number | null
  /** 막대 옆 (완료/전체) 표시 */
  detail: string | null
  title: string
}

const countRate = (key: string, label: string, unit: string, summary: SummaryItem['task']['total']): RateValue => {
  const total = summary.total_count ?? 0
  return {
    key,
    label,
    rate: total ? (summary.count_percentage ?? null) : null,
    detail: total ? `(${summary.completed_count ?? 0}/${total})` : null,
    title: total ? `완료 ${unit} ${summary.completed_count ?? 0}개 ÷ 전체 ${total}개` : `등록된 ${unit} 없음`,
  }
}

/** 기존 상세 현황 표의 진행율 열과 같은 값 (성과 달성률은 성과를 모두 달성한 세부실행과제 수 기준) */
const getRates = (item: SummaryItem, showTasks: boolean): RateValue[] => {
  const budget = item.task_budget.total
  const budgetTarget = budget.total_target_figure ?? 0
  const performance = item.task_performance.total
  const performanceTotal = performance.total_count ?? 0
  return [
    countRate('task_item', '수행업무 진행률', '수행업무', item.task_item.total),
    ...(showTasks ? [countRate('task', '세부실행과제 진행률', '세부실행과제', item.task.total)] : []),
    {
      key: 'budget',
      label: '예산 집행률',
      rate: budgetTarget > 0 ? (budget.figure_percentage ?? null) : null,
      detail:
        budgetTarget > 0 ? `(${formatAmount(budget.total_done_figure ?? 0)}/${formatAmount(budgetTarget)})` : null,
      title:
        budgetTarget > 0
          ? `집행액 ${formatNumber(budget.total_done_figure ?? 0)}원 ÷ 편성액 ${formatNumber(budgetTarget)}원`
          : '편성된 예산 없음',
    },
    {
      key: 'performance',
      label: '성과 달성률',
      rate: performanceTotal ? (performance.count_percentage ?? null) : null,
      detail: performanceTotal ? `(${performance.completed_count ?? 0}/${performanceTotal})` : null,
      title: performanceTotal
        ? `성과 달성 세부실행과제 ${performance.completed_count ?? 0}개 ÷ 성과가 있는 세부실행과제 ${performanceTotal}개`
        : '등록된 성과 없음',
    },
  ]
}

function RateCell({ value }: { value: RateValue }) {
  if (value.rate == null) {
    return (
      <div title={value.title} style={{ ...RATE_BOX_STYLE, gap: 10 }}>
        <span
          aria-hidden
          style={{ display: 'block', flexGrow: 1, height: 8, borderRadius: 4, background: NO_DATA_PATTERN }}
        />
        <span style={{ fontSize: 13, color: strategyColors.mutedText, whiteSpace: 'nowrap' }}>데이터 없음</span>
      </div>
    )
  }
  return (
    <div title={value.title} style={{ ...RATE_BOX_STYLE, gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <RateBar rate={value.rate} />
      </div>
      {value.detail && (
        <span
          style={{
            fontSize: 12,
            color: strategyColors.subText,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value.detail}
        </span>
      )}
    </div>
  )
}

interface StatusTableProps<T extends SummaryItem> {
  title: string
  nameHeader: string
  items: T[]
  loading: boolean
  error: string | null
  showTasks: boolean
  getId: (item: T) => string
  getName: (item: T) => string
  /** 상단 필터로 고른 사업·부서 id (그 줄을 강조한다) */
  selectedId?: string
  onRowClick: (item: T) => void
  onOpenDetail: (item: T) => void
  onOpenList: (item: T) => void
}

function StatusTable<T extends SummaryItem>({
  title,
  nameHeader,
  items,
  loading,
  error,
  showTasks,
  getId,
  getName,
  selectedId,
  onRowClick,
  onOpenDetail,
  onOpenList,
}: StatusTableProps<T>) {
  const rateHeaders = items[0] ? getRates(items[0], showTasks).map((value) => value.label) : []
  const rateCount = showTasks ? 4 : 3
  const gridTemplate = `minmax(220px, 1.6fr) repeat(${rateCount}, minmax(200px, 1fr)) 170px`
  const message = error ?? (loading && items.length === 0 ? '불러오는 중...' : items.length === 0 ? '표시할 항목이 없습니다.' : null)

  return (
    <div className="mt-4">
      <h2
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '18px',
          fontWeight: 700,
          color: designTokens.colors.gray[6],
          marginBottom: '12px',
        }}
      >
        {title}
      </h2>
      <div
        className="bg-white rounded-lg shadow-sm border"
        style={{ borderColor: designTokens.colors.gray[1], color: strategyColors.text }}
      >
        {message ? (
          <div
            className="flex items-center justify-center h-32 text-center"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4],
            }}
          >
            {message}
          </div>
        ) : (
          <div style={{ overflow: 'auto', maxHeight: MAX_TABLE_HEIGHT }}>
            <div style={{ minWidth: 220 + rateCount * 200 + 170, display: 'grid', gridTemplateColumns: gridTemplate }}>
              {[nameHeader, ...rateHeaders, ''].map((label, index) => (
                <span
                  key={index}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    padding: '10px 8px',
                    fontSize: 14,
                    fontWeight: 700,
                    textAlign: 'center',
                    background: strategyColors.surface,
                    borderBottom: `1px solid ${strategyColors.border}`,
                  }}
                >
                  {label}
                </span>
              ))}
              {items.map((item) => {
                const id = getId(item)
                const selected = selectedId === id
                const cellStyle = {
                  padding: CELL_PADDING,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: selected ? designTokens.colors.secondary.light : undefined,
                  borderBottom: `1px solid ${strategyColors.track}`,
                }
                return (
                  // display: contents — 칸은 바깥 grid 에 그대로 놓이고 클릭만 줄 단위로 묶인다
                  <div key={id} style={{ display: 'contents' }} onClick={() => onRowClick(item)}>
                    <span
                      style={{
                        ...cellStyle,
                        fontWeight: 700,
                        fontSize: 15,
                        wordBreak: 'keep-all',
                        borderLeft: selected ? `3px solid ${designTokens.colors.secondary.DEFAULT}` : undefined,
                      }}
                    >
                      {getName(item)}
                    </span>
                    {getRates(item, showTasks).map((value) => (
                      <div key={value.key} style={cellStyle}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <RateCell value={value} />
                        </div>
                      </div>
                    ))}
                    <div
                      style={{ ...cellStyle, justifyContent: 'center', gap: 6 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button size="xsmall" color="light" onClick={() => onOpenDetail(item)}>
                        상세보기
                      </Button>
                      <Button size="xsmall" color="light" onClick={() => onOpenList(item)}>
                        목록보기
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface StatusTablesProps {
  businesses: TaskSummaryTotalBusinessItem[]
  departments: TaskSummaryTotalDepartmentItem[]
  businessLoading: boolean
  departmentLoading: boolean
  businessError: string | null
  departmentError: string | null
  showTasks: boolean
  selectedBusinessId?: string
  selectedDepartmentId?: string
  onBusinessRowClick: (item: TaskSummaryTotalBusinessItem) => void
  onDepartmentRowClick: (item: TaskSummaryTotalDepartmentItem) => void
  onOpenBusinessDetail: (item: TaskSummaryTotalBusinessItem) => void
  onOpenDepartmentDetail: (item: TaskSummaryTotalDepartmentItem) => void
  onOpenList: (scope: 'business' | 'department', id: string, name: string) => void
}

export default function StatusTables(props: StatusTablesProps) {
  return (
    <div className="flex-shrink-0">
      <StatusTable
        title="사업별 현황"
        nameHeader="사업명"
        items={props.businesses}
        loading={props.businessLoading}
        error={props.businessError}
        showTasks={props.showTasks}
        getId={(item) => item.business_id}
        getName={(item) => item.business_name}
        selectedId={props.selectedBusinessId}
        onRowClick={props.onBusinessRowClick}
        onOpenDetail={props.onOpenBusinessDetail}
        onOpenList={(item) => props.onOpenList('business', item.business_id, item.business_name)}
      />
      <div className="mt-6" />
      <StatusTable
        title="부서별 현황"
        nameHeader="부서명"
        items={props.departments}
        loading={props.departmentLoading}
        error={props.departmentError}
        showTasks={props.showTasks}
        getId={(item) => item.department_id}
        getName={(item) => item.department_name}
        selectedId={props.selectedDepartmentId}
        onRowClick={props.onDepartmentRowClick}
        onOpenDetail={props.onOpenDepartmentDetail}
        onOpenList={(item) => props.onOpenList('department', item.department_id, item.department_name)}
      />
    </div>
  )
}
